import React, { useEffect, useMemo, useState } from 'react';
import { Product, Variant } from 'shared/types/product';
import { useFormat } from 'helpers/hooks/useFormat';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import useTouchDevice from 'helpers/hooks/useTouchDevice';
import {
  mobile,
  mobile_large,
  tablet_small,
  tablet_large,
  desktop,
  desktop_xl,
} from 'helpers/utils/preferredScreenSizes';
import Wrapper from '../../../HOC/wrapper';
import Slider from '../../atoms/slider';
import { useAccount, useProduct } from 'frontastic';
import { ResponseBody } from 'shared/types/account/Account';
import ProductTile from './ProductTile';
import { ChevronRightIcon, ChevronLeftIcon } from '@heroicons/react/24/solid';
import Loading from 'components/commercetools-ui/atoms/loading';
import RightIcon from 'public/icons/ag-icons/sliderchevright.svg';
import LeftIcon from 'public/icons/ag-icons/sliderchevleft.svg';

export interface storedVariants {
  product: Product;
  variant: Variant;
}

type ApiResponse = {
  isError: boolean;
  data: {
    total: number;
    items: Product[];
  };
};

type productFromCategory = {
  productFromCategory?: Product[];
};
const RecentlyViewedSlider = ({ productFromCategory }: productFromCategory) => {
  const { isTouchDevice } = useTouchDevice();
  const { formatMessage: formatCommonMessage } = useFormat({ name: 'common' });
  const [recentlyViewd, setRecentlyViewd] = useState<ResponseBody>();
  const { recentlyViewedItem, loggedIn } = useAccount();
  const { addProductToCategory } = useProduct();
  const [startIndex, setStartIndex] = useState(0);
  const [isMobileLarge] = useMediaQuery(mobile_large);
  const [isTabSmall] = useMediaQuery(tablet_small);
  const [isTabLarge] = useMediaQuery(tablet_large);
  // const [isdesktop] = useMediaQuery(desktop);
  // const [isDesktopXL] = useMediaQuery(desktop_xl);
  const visibleItems = useMemo(() => {
    if (isTabLarge) {
      return 4;
    } else if (isTabSmall) {
      return 3.5;
    } else if (isMobileLarge) {
      return 2.5;
    }
    return 1.5;
  }, [isTabLarge, isTabSmall, isMobileLarge]);

  const rawProducts = typeof window !== 'undefined' ? JSON?.parse(localStorage?.getItem('RecentlyViewed') || '[]') : [];

  const retrievedVariants = useMemo(() => {
    return (
      rawProducts
        // 1. Filter published products
        ?.filter((product: Product) => product?.published)
        // 2. Filter in-stock variants
        ?.map((product: Product) => {
          if (product?.variants) {
            const isCustomFitProduct = product?.variants?.some((variant) => variant?.attributes?.isCustomFit === true);
            product.variants = product?.variants?.filter((variant) => (isCustomFitProduct ? true : variant?.isOnStock));
          }
          return product;
        })
        // 3. Remove products with no variants left
        ?.filter((product: Product) => product?.variants?.length > 0)
    );
  }, []);

  useEffect(() => {
    if (loggedIn) {
      const handleRecentlyViewed = async () => {
        const GetrecentlyViewed = await recentlyViewedItem(rawProducts);
        setRecentlyViewd(GetrecentlyViewed);
      };
      handleRecentlyViewed();
    }
  }, []);
  // useEffect(() => {
  //   const handleRecentlyViewedCategory = async () => {
  //     if (retrievedVariants.length > 0) {
  //       await addProductToCategory(retrievedVariants);
  //     }
  //   };
  //   handleRecentlyViewedCategory();
  // }, [retrievedVariants.length]);
  const skus = recentlyViewd?.body?.custom?.fields?.recently_viewed_product_sku_List;
  const shouldLoop = useMemo(() => {
    if (productFromCategory?.length || retrievedVariants?.length === 2) return false;
    if (productFromCategory?.length || retrievedVariants?.length <= 2) return visibleItems === 1.5;
    if (productFromCategory?.length || retrievedVariants?.length === 3) return visibleItems <= 2.5;
    if (productFromCategory?.length || retrievedVariants?.length === 4) return visibleItems <= 3.5 && !isTabLarge;
    return !isTabLarge;
  }, [productFromCategory?.length, retrievedVariants?.length, visibleItems, isTabLarge]);

  const totalItems = useMemo(() => {
    return productFromCategory && productFromCategory?.length > 0
      ? productFromCategory?.length
      : retrievedVariants?.length;
  }, [productFromCategory?.length, retrievedVariants?.length]);

  const renderItems = (items: Product[]) => {
    const missingItem = !items?.some((item) => item?.productKey === retrievedVariants[0]?.productKey)
      ? retrievedVariants?.[0]
      : null;
    if (items.length > 0) {
      const itemWidth = 100 / visibleItems;
      const normalizedStartIndex = startIndex % totalItems;

      if (items) {
        const sortedItems = [...items]?.sort((a, b) => {
          const parseTimestamp = (timestamp: any) => {
            if (!timestamp) return 0;

            const [datePart, timePart] = timestamp.split(' ');
            const [day, month, year] = datePart.split('/').map(Number);
            const [hours, minutes] = timePart.split(':').map(Number);

            const date = new Date(year, month - 1, day, hours, minutes);

            return date.getTime();
          };

          const timestampA = parseTimestamp(a?.variants?.[0]?.attributes?.recently_viewed_timestamp);
          const timestampB = parseTimestamp(b?.variants?.[0]?.attributes?.recently_viewed_timestamp);

          return timestampB - timestampA;
        });
        const iscategoryUpdated = sortedItems.includes(retrievedVariants?.[0]);
        const updatedItems = missingItem ? [missingItem, ...sortedItems] : sortedItems; // Add missingItem at the start

        if (shouldLoop && sortedItems?.length > 0) {
          const loopedItems = [...updatedItems, ...updatedItems, ...updatedItems];
          const start = sortedItems?.length + normalizedStartIndex;
          const end = start + Math?.ceil(visibleItems);

          return loopedItems?.slice(start, end)?.map((item, index) => (
            <div
              key={index}
              className="flex flex-shrink-0 items-stretch transition-transform duration-300"
              style={{ width: `${itemWidth}%`, minWidth: `${itemWidth}%` }}
            >
              <ProductTile product={item} iscategoryUpdated={iscategoryUpdated} />
            </div>
          ));
        }
        return updatedItems
          .slice(normalizedStartIndex, normalizedStartIndex + Math.ceil(visibleItems))
          .map((item: Product, index: number) => (
            <div
              key={index}
              className="flex flex-shrink-0 items-stretch transition-transform duration-300"
              style={{ width: `${itemWidth}%`, minWidth: `${itemWidth}%` }}
            >
              <ProductTile product={item} iscategoryUpdated={iscategoryUpdated} />
            </div>
          ));
      }
    }
  };

  const handleLeftClick = () => {
    setStartIndex((prevIndex) => (shouldLoop ? (prevIndex - 1 + totalItems) % totalItems : Math.max(0, prevIndex - 1)));
  };

  const handleRightClick = () => {
    const maxIndex = Math.max(totalItems - visibleItems, 0);
    setStartIndex((prevIndex) => (shouldLoop ? (prevIndex + 1) % totalItems : Math.min(maxIndex, prevIndex + 1)));
  };

  return (
    <>
      {productFromCategory?.length === 0 ? (
        <Loading />
      ) : (
        <Wrapper clearDefaultStyles className="my-30 max-w-[104rem] xl:mx-50 2xl:mx-auto">
          <div
            className={`flex items-center ${
              isTabSmall || totalItems === 1 ? 'relative justify-center' : 'justify-between'
            }`}
          >
            <h3 className="align-center mx-20 mb-30 flex justify-center text-center font-body text-16 font-bold leading-7 md:text-24 lg:mx-0">
              {formatCommonMessage({
                id: 'recently.viewed',
                defaultMessage: 'RECENTLY VIEWED',
              })}
            </h3>
            {totalItems > visibleItems && (
              <div className={`mb-20 flex items-center justify-end gap-x-6 ${isTabSmall ? 'absolute right-[2%]' : ''}`}>
                <button
                  className={`border-1 flex h-[48px] w-[48px] items-center justify-center rounded-full border bg-white p-3 text-gray-700 ${
                    startIndex === 0 && !shouldLoop ? 'border-[#E7E7E7]' : 'border-black'
                  }`}
                  onClick={handleLeftClick}
                  disabled={!shouldLoop && startIndex === 0}
                >
                  <LeftIcon />
                  {/* <ChevronLeftIcon className="h-20 w-20" stroke="#1a1a1a" strokeWidth="1" fill="#1a1a1a" /> */}
                </button>
                <button
                  className={`border-1 flex h-[48px] w-[48px] items-center justify-center rounded-full border bg-white p-3 text-gray-700 ${
                    startIndex >= Math.max(totalItems - visibleItems, 0) && !shouldLoop
                      ? 'border-[#E7E7E7]'
                      : 'border-black'
                  }`}
                  onClick={handleRightClick}
                  disabled={!shouldLoop && startIndex >= Math.max(totalItems - visibleItems, 0)}
                >
                  <RightIcon />
                  {/* <ChevronRightIcon className="h-16 w-8 border-[2px]" stroke="#1a1a1a" strokeWidth="1" fill="#1a1a1a" /> */}
                </button>
              </div>
            )}
          </div>

          <div
            className={`ml-10 flex items-stretch gap-5 overflow-hidden md:ml-0 ${
              totalItems === 1 ? 'justify-center sm:justify-start 2xl:ml-[4rem]' : ''
            } ${totalItems > 4 ? 'justify-around' : 'justify-start md:ml-[4rem]'}`}
          >
            {productFromCategory && productFromCategory?.length > 0
              ? renderItems(productFromCategory?.map((item) => item))
              : retrievedVariants && renderItems(retrievedVariants)}
          </div>
        </Wrapper>
      )}
    </>
  );
};

export default RecentlyViewedSlider;
